import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import Conditional from '../../shared/conditional/conditional';
import FeedbackMessage from '../feedback-message/feedback-message';
import styles from './tooltip.module.scss';

type TooltipProps = {
  children: ReactNode;
  message?: ReactNode;
  backgroundColor?: string;
  type?: 'error' | 'info';
};

const GAP_TO_CONTAINER = 5;

export function Tooltip({
  children,
  message,
  backgroundColor = 'white',
  type,
}: TooltipProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);

  const updatePosition = useCallback(() => {
    if (!containerRef.current || !tooltipRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    let left = containerRect.left;
    // If tooltip would overflow right edge, align to right
    if (left + tooltipRect.width > viewportWidth - 20) {
      left = containerRect.left + containerRect.width - tooltipRect.width;
    }

    setTooltipPosition({
      top: containerRect.bottom + GAP_TO_CONTAINER,
      left: left + window.scrollX,
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible, updatePosition]);

  const tooltipContent = useMemo(() => {
    if (!isVisible) return null;

    let content: ReactNode = message;

    switch (type) {
      case 'error':
        content = (
          <FeedbackMessage type={type}>
            <p>{message}</p>
          </FeedbackMessage>
        );
        break;
    }

    return createPortal(
      <div
        ref={tooltipRef}
        className={styles.tooltipContent}
        style={{
          backgroundColor,
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        {content}
      </div>,
      document.body
    );
  }, [
    backgroundColor,
    isVisible,
    message,
    tooltipPosition.left,
    tooltipPosition.top,
    type,
  ]);

  return (
    <Conditional condition={!!message}>
      <Conditional.True>
        <div
          className={styles.tooltipContainer}
          ref={containerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onScroll={updatePosition}
        >
          {children}
          {tooltipContent}
        </div>
      </Conditional.True>
      <Conditional.False>{children}</Conditional.False>
    </Conditional>
  );
}
